import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/Badges";
import { Car, FileText, User, Save, ScanLine } from "lucide-react";
import { toast } from "sonner";
import ScanRegistrationDialog from "@/components/ScanRegistrationDialog";
import NotesPanel from "@/components/NotesPanel";
import SendEmailDialog from "@/components/SendEmailDialog";
import { Mail, StickyNote } from "lucide-react";

export default function CustomerDetailDialog({ open, onOpenChange, customerId, canEdit, onSaved }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", notes: "" });
  const [scanOpen, setScanOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const reload = async () => {
    if (!customerId) return;
    const { data } = await api.get(`/customers/${customerId}`);
    setData(data);
  };

  useEffect(() => {
    if (!open || !customerId) return;
    (async () => {
      const { data } = await api.get(`/customers/${customerId}`);
      setData(data);
      setForm({
        name: data.customer.name || "", phone: data.customer.phone || "",
        email: data.customer.email || "", address: data.customer.address || "",
        notes: data.customer.notes || "",
      });
    })();
  }, [open, customerId]);

  const save = async () => {
    try {
      await api.patch(`/customers/${customerId}`, form);
      toast.success(t("common.save"));
      onSaved && onSaved();
      const { data } = await api.get(`/customers/${customerId}`);
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (!data) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#21252E] border-[#2E3241] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <User className="h-5 w-5 text-blue-400" /> {t("customers.detail")}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="info" className="space-y-3">
          <TabsList className="bg-[#171B24] border border-[#2E3241]">
            <TabsTrigger value="info">{t("common.name")}</TabsTrigger>
            <TabsTrigger value="vehicles" data-testid="customer-tab-vehicles">
              {t("customers.linked_vehicles")} ({data.vehicles.length})
            </TabsTrigger>
            <TabsTrigger value="contracts" data-testid="customer-tab-contracts">
              {t("customers.linked_contracts")} ({data.contracts.length})
            </TabsTrigger>
            <TabsTrigger value="notes" data-testid="customer-tab-notes">
              <StickyNote className="h-3.5 w-3.5 mr-1" />{t("notes.title")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="surface rounded-lg p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">{t("common.name")}</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  disabled={!canEdit} className="bg-[#171B24] border-[#2E3241]" data-testid="cust-detail-name" /></div>
              <div><Label className="text-xs">{t("common.phone")}</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  disabled={!canEdit} className="bg-[#171B24] border-[#2E3241]" /></div>
              <div><Label className="text-xs">{t("common.email")}</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  disabled={!canEdit} className="bg-[#171B24] border-[#2E3241]" /></div>
              <div><Label className="text-xs">{t("common.address")}</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  disabled={!canEdit} className="bg-[#171B24] border-[#2E3241]" /></div>
            </div>
            <div>
              <Label className="text-xs">{t("common.notes")}</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                disabled={!canEdit} rows={3} className="bg-[#171B24] border-[#2E3241]" data-testid="cust-detail-notes" />
            </div>
          </TabsContent>

          <TabsContent value="vehicles" className="space-y-2">
            {canEdit && (
              <div className="flex justify-end">
                <Button
                  size="sm" variant="outline"
                  onClick={() => setScanOpen(true)}
                  className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
                  data-testid="cust-scan-registration-btn"
                >
                  <ScanLine className="h-4 w-4 mr-1" /> {t("scan.button")}
                </Button>
              </div>
            )}
            {data.vehicles.length === 0 && <div className="text-sm text-zinc-500">{t("common.empty")}</div>}
            {data.vehicles.map((v) => (
              <div key={v.id} className="surface rounded-lg p-3 flex items-center justify-between" data-testid={`cust-vehicle-${v.id}`}>
                <div className="flex items-center gap-3">
                  <Car className="h-4 w-4 text-blue-400" />
                  <div>
                    <div className="font-display font-semibold text-sm">{v.brand} {v.model}</div>
                    <div className="text-xs text-zinc-500 font-mono-stat">{v.license_plate} · VIN {v.vin || "—"} · {v.mileage} km</div>
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="contracts" className="space-y-2">
            {data.contracts.length === 0 && <div className="text-sm text-zinc-500">{t("common.empty")}</div>}
            {data.contracts.map((c) => (
              <div
                key={c.id}
                onClick={() => { onOpenChange(false); nav(`/contracts/${c.id}`); }}
                className="surface rounded-lg p-3 flex items-center justify-between cursor-pointer hover:bg-[#2A2F3A]"
                data-testid={`cust-contract-${c.number}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-4 w-4 text-blue-400" />
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-sm text-blue-300">{c.number}</div>
                    <div className="text-xs text-zinc-400 truncate">{c.title || "—"} · {c.planned_date || ""}</div>
                  </div>
                </div>
                <StatusBadge status={c.status} />
              </div>
            ))}
          </TabsContent>

          <TabsContent value="notes" className="surface rounded-lg p-4">
            <NotesPanel entityType="customer" entityId={customerId} />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          {canEdit && data.customer?.email && (
            <Button
              variant="outline"
              onClick={() => setEmailOpen(true)}
              className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
              data-testid="cust-email-btn"
            >
              <Mail className="h-4 w-4 mr-1" /> {t("emails.send")}
            </Button>
          )}
          {canEdit && (
            <Button onClick={save} className="bg-blue-500 hover:bg-blue-600" data-testid="cust-detail-save">
              <Save className="h-4 w-4 mr-1" /> {t("common.save")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      <ScanRegistrationDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        customers={data.customer ? [data.customer] : []}
        presetCustomerId={customerId}
        onSaved={() => { reload(); onSaved && onSaved(); }}
        allowCustomerCreate={false}
      />

      <SendEmailDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        customerId={customerId}
        customerName={data.customer?.name}
        customerEmail={data.customer?.email}
      />
    </Dialog>
  );
}
